import {useCallback, useEffect, useMemo, useState} from 'react'
import {useForm} from 'react-hook-form'
import {useNavigation} from 'react-navi'
import {useAppState} from '../../../../state'
import {AlarmSetting, AlarmMethod, Recipient, WeekSchedule} from '../../../../state/rest'
import {useDeviceField, useRestaurantListField} from '../../../../config/utils'
import {useWeekScheduleField} from '../../../Organisms/Scheduler/useWeekScheduleList'
import i18n from 'i18next'
import {AppliancesForChainByCategory} from 'state/appliances/state'

export interface AlarmRuleFormValues {
  name: string
  delayInMin: number
  sites: string[]
  alarmMethods: string[]
  schedules: WeekSchedule[]
  recipients: Recipient[]
  extRecipients: Recipient[]
  equipment: string[]
}

export const useAlarmRuleForm = (saved?: Partial<AlarmSetting>) => {
  const {actions, state} = useAppState()
  const {navigate} = useNavigation()

  const backPath = '/settings/alarms'

  const chainId = state.selectedChainId
  useEffect(() => {
    if (chainId)
      actions.v1.appliances.getAppliancesForChain({
        chainId: chainId
      })
  }, [actions.v1.appliances, chainId])

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: {isValid},
    setValue,
    errors
  } = useForm<AlarmRuleFormValues>({
    defaultValues: {
      name: saved?.name ?? '',
      delayInMin: saved?.delayInMin ?? 0,
      sites: saved?.sites?.map(s => s.id) ?? [],
      alarmMethods: saved?.alarmMethods ?? [AlarmMethod.EMAIL],
      schedules: saved?.schedules ?? [],
      recipients: saved?.recipients ?? [],
      extRecipients: saved?.extRecipients ?? [],
      equipment: saved?.equipment?.map(e => e.id) ?? []
    },
    mode: 'onChange'
  })

  useEffect(() => {
    register({name: 'sites'})
    register({name: 'equipment'})
    register({name: 'recipients'})
    register({name: 'extRecipients'})
    register({name: 'schedules'})
    register({name: 'alarmMethods'})
  }, [register])

  const rc = useRestaurantListField({watch, setValue}, saved?.sites)

  const rawEquipment =
    state.v1.appliances.chainWideAppliances.data.appliancesForChainByCategory ??
    ({
      cooler: [],
      freezer: [],
      dishwasher: [],
      storage: [],
      'waste-scale': []
    } as AppliancesForChainByCategory)

  const selectedIds = rc.list.map(s => s.id)

  const devices = useDeviceField({watch, setValue}, saved?.equipment || [], rawEquipment, selectedIds)

  const amc = useAlarmMethodField({watch, setValue}, saved ? saved.alarmMethods : undefined)
  const uc = useRecipientField(
    {watch, setValue},
    saved ? saved.recipients : undefined,
    saved ? saved.extRecipients : undefined
  )
  const sc = useWeekScheduleField({watch, setValue}, saved ? saved.schedules : undefined)

  const [loading, setLoading] = useState(false)

  const onSave = handleSubmit(async values => {
    setLoading(true)
    try {
      console.log('Saving', values)
      const sitesPayload = values.sites.map(id => ({id}))
      const payload = {
        name: values.name,
        delayInMin: values.delayInMin,
        alarmMethods: values.alarmMethods,
        schedules: values.schedules,
        recipients: values.recipients,
        extRecipients: values.extRecipients,
        sites: sitesPayload,
        equipment: values.equipment
      }
      if (saved?.id) {
        Object(payload).id = saved.id
        await actions.updateAlarmSetting({id: saved.id, ...payload})
      } else {
        await actions.createAlarmSetting(payload)
      }
      navigate(backPath)
    } catch (err) {
      console.error('save failed', err)
    } finally {
      setLoading(false)
    }
  })

  const onDelete = useCallback(async () => {
    if (!saved?.id) return
    setLoading(true)
    try {
      await actions.deleteAlarmSetting(saved.id)
      navigate(backPath)
    } catch (err) {
      console.error('Delete failed', err)
    } finally {
      setLoading(false)
    }
  }, [actions.deleteAlarmSetting, navigate, saved?.id])

  const onCancel = useCallback(() => navigate(backPath), [navigate])

  return {
    register,
    control,
    errors,
    isValid,
    loading,
    rc,
    devices,
    sc,
    amc,
    uc,
    onSave,
    onDelete,
    onCancel,
    backPath
  }
}

export const getAlarmMethodName = (methodKey: string) => {
  switch (methodKey) {
    case AlarmMethod.SMS: {
      return i18n.t('alarms:labels.textMessage', 'Text message')
    }
    default: {
      return i18n.t('alarms:labels.email', 'Email')
    }
  }
}

export const getRecipientName = (user: Recipient) =>
  user.name || `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || ''

export const getRecipientsNames = (recipients: Recipient[], limit = Infinity) => {
  if (recipients.length <= limit) {
    return recipients.map(getRecipientName).join(', ')
  }
  return `${getRecipientName(recipients[0])}, ${recipients.length - 1} ${i18n.t(
    'common:labels.othersAmount',
    'others'
  )}`
}

type RHFBridge = {watch: any; setValue: any}

export function useAlarmMethodField(form: RHFBridge, saved?: AlarmMethod[]) {
  const options = useMemo(() => {
    return (Object.values(AlarmMethod) as (string | number)[]).filter(v => typeof v === 'string') as AlarmMethod[]
  }, [])

  const getInitial = () => {
    const fromForm = (form.watch && form.watch('alarmMethods')) as AlarmMethod[] | undefined
    if (Array.isArray(fromForm) && fromForm.length) return fromForm
    if (Array.isArray(saved) && saved.length) return saved
    return [AlarmMethod.EMAIL]
  }

  const initial = getInitial()

  const [selected, setSelected] = useState<AlarmMethod[]>(initial)

  useEffect(() => {
    form.setValue('alarmMethods', selected)
  }, [selected])

  const toggle = useCallback((id: AlarmMethod) => {
    setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
  }, [])

  return {selected, toggle, options}
}

export function useRecipientField(form: RHFBridge, saved?: Recipient[], extSaved?: Recipient[]) {
  const initialRecipients = useMemo<Recipient[]>(() => {
    const fromForm = (form.watch && form.watch('recipients')) as Recipient[] | undefined
    return Array.isArray(fromForm) && fromForm.length ? fromForm : saved ?? []
  }, [form.watch, saved])

  const initialExtRecipients = useMemo<Recipient[]>(() => {
    const fromForm = (form.watch && form.watch('extRecipients')) as Recipient[] | undefined
    return Array.isArray(fromForm) && fromForm.length ? fromForm : extSaved ?? []
  }, [form.watch, extSaved])

  const [recipients, setRecipients] = useState<Recipient[]>(initialRecipients)
  const [extRecipients, setExtRecipients] = useState<Recipient[]>(initialExtRecipients)

  const [isOpen, setIsOpen] = useState(false)
  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])
  const toggleModal = useCallback(() => setIsOpen(v => !v), [])

  useEffect(() => {
    form.setValue('recipients', recipients)
  }, [recipients])

  useEffect(() => {
    form.setValue('extRecipients', extRecipients)
  }, [extRecipients])

  const keyOf = (r: Recipient) => r.id ?? r.email ?? r.phoneNumber ?? JSON.stringify(r)

  const dedupe = (list: Recipient[]) => {
    const seen = new Set<string>()
    return list.filter(r => {
      const k = keyOf(r)
      if (seen.has(k)) return false
      seen.add(k)
      return true
    })
  }

  const handleSave = useCallback((r: Recipient[], er: Recipient[]) => {
    setRecipients(dedupe(r))
    setExtRecipients(dedupe(er))
    setIsOpen(false)
  }, [])

  return {recipients, extRecipients, isOpen, toggleModal, handleSave, open, close}
}
